import { ManufacturingOrder } from "../components/full-time-scheduler/production-plan/utils/calculations";
import axios from "../utils/api-client";

export interface ExtractedMO {
    // Raw values from AI
    rawProduct: string
    rawQuantity: string
    rawWorkstation: string
    rawStartTime: string
    rawEndTime: string

    // Matched/parsed values
    productId: number | null
    productName: string | null
    sku: string
    quantity: number | null
    workstation: string | null
    startTime: string | null
    endTime: string | null

    // Confidence score (0-100)
    confidence: number

    // Validation
    isValid: boolean
    validationErrors: string[]
}

export interface AIParseResponse {
    success: boolean
    extractedOrders: ExtractedMO[]
    processingTimeMs: number
    warnings: string[]
    error?: string
    // For multi-sheet Excel files
    requiresSheetSelection?: boolean
    availableSheets?: string[]
}

export interface AvailableData {
    products: { id: number; sku: string; name: string }[]
    workcenters: string[]
}

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/ProductionPlan`;

export const aiUploadApi = {
    /**
     * Parse a document (image, PDF, Excel, Word) using AI to extract Manufacturing Orders
     * @param selectedSheet - Optional: specific sheet name for multi-sheet Excel files
     */
    parseDocument: async (
        file: File,
        weekStartDate: string,
        availableData: AvailableData,
        selectedSheet?: string
    ): Promise<AIParseResponse> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("weekStartDate", weekStartDate);
        formData.append("products", JSON.stringify(availableData.products));
        formData.append("workcenters", JSON.stringify(availableData.workcenters));
        if (selectedSheet) {
            formData.append("selectedSheet", selectedSheet);
        }

        const response = await axios.post<AIParseResponse>(
            `${API_BASE_URL}/ai-parse`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                timeout: 300000, // 5 minute timeout for AI processing large files
            }
        );

        return response.data;
    },

    /**
     * Convert extracted MOs to the format needed for batch creation
     */
    convertToManufacturingOrders: (
        extractedOrders: ExtractedMO[]
    ): Omit<ManufacturingOrder, "id">[] => {
        return extractedOrders
            .filter((order) => order.isValid)
            .map((order) => ({
                productId: order.productId,
                sku: order.sku,
                quantity: order.quantity || 0,
                workstation: order.workstation || "",
                startTime: order.startTime || "",
                endTime: order.endTime,
            }));
    },
};


